import type { Document } from '@/types/pursuit/Document'

/**
 * Room type for editor v3 (SuperDoc) documents. Mirrors `TiptapDocumentType`
 * on the backend, which mints `tiptap_id` as `{type}/{org}/{user}/{id}`.
 */
const SUPERDOC_ROOM_TYPE = 'superdoc'

/**
 * Whether a document's `tiptap_id` names an editor v3 room.
 *
 * The room type is the first path segment. A v3 room holds a SuperDoc Yjs
 * document with no Tiptap schema in it, so v2 cannot open it — this is what
 * decides which editor a document routes to.
 */
export const isSuperDocDocument = (tiptapId: string | undefined): boolean =>
  tiptapId?.split('/')[0] === SUPERDOC_ROOM_TYPE

/**
 * The editor page a document opens in, as a route pattern. A caller holding the
 * document goes straight there; the page one level up serves links that arrive
 * with nothing but an id, and fetches the document to answer the same question.
 */
export const editorPathname = (tiptapId: string | undefined): string =>
  `/orgs/[orgId]/pursuits/[pursuitId]/documents/[documentId]/${
    isSuperDocDocument(tiptapId) ? 'v3' : 'v2'
  }`

/** The same page as a real URL, for a caller that pushes a path. */
export const editorHref = ({
  document,
  orgId,
  pursuitId,
}: {
  document: Pick<Document, 'id' | 'tiptap_id'>
  orgId: string
  pursuitId: string
}): string =>
  `/orgs/${orgId}/pursuits/${pursuitId}/documents/${document.id}/${
    isSuperDocDocument(document.tiptap_id) ? 'v3' : 'v2'
  }`
